import React, { useState, useEffect } from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import { useNavigate, useLocation } from 'react-router-dom';

/** Los que no caben en los 130 por defecto, por lo largo de su etiqueta. */
const ANCHOS = { editor: 150 };

/* `valorPorDefecto` y `valorTodos` (Fase D) — para filtros que NO nacen vacios.
 *
 * Por defecto este selector trata «sin parametro en la URL» y «Todos» como lo mismo: la opcion
 * Todos vale '' y `handleChange` BORRA el parametro. Eso funciona mientras el filtro empiece sin
 * filtrar.
 *
 * El filtro de Estado ya no empieza asi: nace en «Alta». Y entonces las dos cosas dejan de poder
 * ser la misma, porque **elegir «Todos» borraria el parametro y el valor por defecto volveria a
 * aplicarse** — el usuario no podria salir del defecto.
 *
 * Con `valorTodos` la opcion Todos escribe un valor explicito en la URL en vez de borrarla, y con
 * `valorPorDefecto` el selector MUESTRA lo que de verdad se esta filtrando cuando el parametro no
 * esta. Sin lo segundo el desplegable diria «Todos» sobre una lista filtrada, que es una pantalla
 * que miente.
 *
 * Los demas filtros no pasan ninguno de los dos y se comportan igual que siempre.
 */
// `sx` (opcional, 31/10): se AÑADE encima del estilo de siempre. Sin él, el selector es igual que
// antes en todas las pantallas que lo usan; el listado de Personas lo pide elástico.
// `size` (opcional, 31/10): el de MUI. Sin él, el tamaño de siempre; el listado de Personas pide
// `small` (40px, la altura de sus botones). Va como prop y no por `sx` porque `small` también
// recoloca la etiqueta flotante, y eso no se reproduce limpio a mano.
export default function SelectDinamicoLista({ opciones = [], parametroURL, etiqueta, onChange, anchuraCompletaMovil = false, valorPorDefecto = '', valorTodos = '', sx, size }) {
    const navigate = useNavigate();
    const location = useLocation();

    const conseguirValorURL = () => {
        const params = new URLSearchParams(location.search);
        const v = params.get(parametroURL);
        return v === null ? valorPorDefecto : v;
    };

    const [valorSeleccionado, setValorSeleccionado] = useState(conseguirValorURL);
    //const cambioURL = CambioDeURL(parametroURL);
    
    const debeDeSerAnchuraCompleta = anchuraCompletaMovil || parametroURL === 'activo';

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const enURL = params.get(parametroURL);
        const nuevoValor = enURL === null ? valorPorDefecto : enURL;
        setValorSeleccionado(nuevoValor);
        if (onChange) {
            onChange(nuevoValor);
        }
    }, [location.search, parametroURL, onChange, valorPorDefecto]);

    const handleChange = (event) => {
        const nuevoValor = event.target.value;
        const url = new URL(window.location);
        const params = new URLSearchParams(url.search);
        params.set(parametroURL, nuevoValor);
        if (nuevoValor === "" || nuevoValor === null) {
            params.delete(parametroURL);
        }
        if(parametroURL === "empresa" && nuevoValor != "") {
            params.delete("departamento");
            params.delete("mostrar_dependientes");
        }
        navigate(`${url.pathname}?${params.toString()}`);
        
        if (onChange) {
            onChange(nuevoValor);
        }
    };

    return (
        <FormControl
            size={size}
            sx={{ 
                ml: { xs: 0, sm: 0.5 },
                mb: { xs: 1, sm: 0 },
                width: { 
                    xs: debeDeSerAnchuraCompleta ? '100%' : 'calc(50% - 4px)', 
                    // El ancho lo decide el parámetro, siguiendo el criterio que ya había. Los
                    // 130 por defecto valen para etiquetas cortas —«Negocio», «Estado»,
                    // «Empresa»—; una que no quepa se corta, y un filtro cuyo nombre no se lee
                    // no se usa.
                    sm: ANCHOS[parametroURL] || 130
                },
                ...sx
            }}
        >
            <InputLabel id={`${parametroURL}-select-label`}>{etiqueta}</InputLabel>
            <Select
                labelId={`${parametroURL}-select-label`}
                id={`${parametroURL}-select`}
                value={valorSeleccionado}
                label={etiqueta}
                onChange={handleChange}
                disabled={opciones.length === 0}
            >
                <MenuItem key={null} value={valorTodos}>Todos</MenuItem>
                {opciones?.map((opcion) => (
                    <MenuItem key={opcion.Id} value={opcion.urlCat || opcion.ref || opcion.Id}>
                        {parametroURL == "editor" 
                            ? `${opcion.nombre} ${opcion.apellidos}`
                            : opcion.categoria || opcion.nombre || opcion.Id}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
